import { AxiosResponse } from 'axios';
import { apiClient, getAuthHeaders } from './index';
import { TokenCategory } from '../../stores/data/types';
import {
  TokenState,
  TweetStatsResponse,
  TweetStatsTimelineResponse,
  UpdateTweetStatsConfigRequest,
  UpdateTweetStatsConfigResponse,
  KeywordAutocompleteResponse,
  GetAllKeywordsResponse,
  SearchTokensByKeywordsResponse,
  UpdateTokenKeywordsResponse,
  SocialSyncTargetDto,
} from './types';

export const tokenStateApi = {

  /**
   * Token State를 업데이트합니다.
   * @param tokenAddress 토큰 주소
   * @param request 업데이트 요청 (memo, category, is_archived, status)
   * @returns 업데이트된 Token State
   */
  async updateTokenState(
    tokenAddress: string,
    request: { memo?: string; category?: TokenCategory; isArchived?: boolean; }
  ): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.put(
        `/fe-management-system/token-states/${tokenAddress}`,
        request,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ PUT /fe-management-system/token-states 에러:', error);
      throw error;
    }
  },

  /**
   * 특정 토큰 주소의 상태를 조회합니다.
   * @param tokenAddress 조회할 토큰 주소
   * @returns Token State
   */
  async getTokenState(tokenAddress: string): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/fe-management-system/token-states/${tokenAddress}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states/:tokenAddress 에러:', error);
      throw error;
    }
  },

  /**
   * 토큰에 연결된 SocialSyncState(동기화 대상) 목록을 조회합니다.
   * (sol-af: SocialSyncStateController — GET /token-states/:tokenAddress/sync-targets)
   */
  async listSocialSyncTargets(tokenAddress: string): Promise<SocialSyncTargetDto[]> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/token-states/${encodeURIComponent(tokenAddress)}/sync-targets`,
        {
          headers: getAuthHeaders(),
        },
      );

      const payload = response.data?.data;
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      console.error(
        `❌ GET /token-states/${tokenAddress}/sync-targets 에러:`,
        error,
      );
      throw error;
    }
  },

  async createSyncTarget(
    tokenAddress: string,
    sourceType: string,
    sourceKey: string,
    tokenSymbol: string,
  ): Promise<SocialSyncTargetDto> {
    const trimmed = tokenSymbol.trim();
    const token_symbol = trimmed.length > 0 ? trimmed : 'UNKNOWN';
    try {
      const response: AxiosResponse<any> = await apiClient.post(
        `/token-states/${encodeURIComponent(tokenAddress)}/sync-targets`,
        { source_type: sourceType, source_key: sourceKey, token_symbol },
        { headers: getAuthHeaders() },
      );
      return response.data?.data;
    } catch (error) {
      console.error(
        `❌ POST /token-states/${tokenAddress}/sync-targets 에러:`,
        error,
      );
      throw error;
    }
  },

  async updateSyncResultNote(
    targetId: string,
    resultIndex: number,
    note: string,
  ): Promise<SocialSyncTargetDto> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/sync-targets/${encodeURIComponent(targetId)}/results/${resultIndex}/note`,
        { note },
        { headers: getAuthHeaders() },
      );
      return response.data?.data;
    } catch (error) {
      console.error(
        `❌ PATCH /sync-targets/${targetId}/results/${resultIndex}/note 에러:`,
        error,
      );
      throw error;
    }
  },

  /**
   * SocialSyncState의 enabled 플래그를 갱신합니다.
   * (sol-af: PATCH /sync-targets/:targetId — UpdateSyncTargetReqDto)
   */
  async updateSyncTargetEnabled(
    targetId: string,
    enabled: boolean,
  ): Promise<SocialSyncTargetDto> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/sync-targets/${encodeURIComponent(targetId)}`,
        { enabled },
        { headers: getAuthHeaders() },
      );
      return response.data?.data;
    } catch (error) {
      console.error(`❌ PATCH /sync-targets/${targetId} (enabled) 에러:`, error);
      throw error;
    }
  },

  /**
   * 키워드로 토큰을 검색합니다 (토큰 어드레스, 심볼, 이름, 메모).
   * @param keyword 검색 키워드
   * @returns 검색된 토큰 주소 목록
   */
  async searchTokens(keyword: string): Promise<string[]> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/fe-management-system/token-states/search/${encodeURIComponent(keyword)}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states/search 에러:', error);
      throw error;
    }
  },

  /**
   * 모든 토큰 상태 목록을 조회합니다 (아카이브 필터 가능).
   * @param archived "true" = 아카이브된 토큰만, 생략 = 전체
   * @returns Token State 목록
   */
  async getAllTokenStates(archived?: string): Promise<TokenState[]> {
    try {
      const params: Record<string, string | number | undefined> = {};
      if (archived) {
        params.archived = archived;
      }

      const response: AxiosResponse<{ data: TokenState[] }> = await apiClient.get(
        '/fe-management-system/token-states',
        {
          params,
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states 에러:', error);
      throw error;
    }
  },

  /**
   * 토큰 상태 목록을 페이지네이션 + 정렬 + 검색/필터로 조회합니다.
   * @param params page, limit, sortField, sortOrder, archived, searchKeyword, keywords, category, status
   * @returns { data: TokenState[], meta: { total, page, limit, totalPages } }
   */
  async getTokenStatesPaginated(params: {
    page: number;
    limit?: number;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
    archived?: string;
    searchKeyword?: string;
    keywords?: string[];
    category?: string[];
    status?: string[];
  }): Promise<{
    data: TokenState[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    try {
      const {
        page,
        limit = 50,
        sortField,
        sortOrder,
        archived,
        searchKeyword,
        keywords,
        category,
        status,
      } = params;
      const queryParams: Record<string, string | number> = {
        page,
        limit,
      };
      if (sortField) queryParams.sortField = sortField;
      if (sortOrder) queryParams.sortOrder = sortOrder;
      if (archived) queryParams.archived = archived;
      if (searchKeyword?.trim()) queryParams.searchKeyword = searchKeyword.trim();
      if (keywords?.length) queryParams.keywords = keywords.join(',');
      if (category?.length) queryParams.category = category.join(',');
      if (status !== undefined) queryParams.status = status.length ? status.join(',') : '';

      const response: AxiosResponse<{
        data: TokenState[];
        meta: { total: number; page: number; limit: number; totalPages: number };
      }> = await apiClient.get('/fe-management-system/token-states', {
        params: queryParams,
        headers: getAuthHeaders(),
      });

      return {
        data: response.data.data,
        meta: response.data.meta ?? {
          total: response.data.data.length,
          page,
          limit,
          totalPages: 1,
        },
      };
    } catch (error) {
      console.error(
        '❌ GET /fe-management-system/token-states (paginated) 에러:',
        error
      );
      throw error;
    }
  },

  /**
   * 아카이브된 토큰 상태 목록을 조회합니다.
   * @returns 아카이브된 Token State 목록
   */
  async getArchivedTokenStates(): Promise<TokenState[]> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        '/fe-management-system/token-states',
        {
          params: {
            archived: 'true',
          },
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states?archived=true 에러:', error);
      throw error;
    }
  },
  async restartFetchingFailedTokens(): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.post(
        `/fe-management-system/token-states/fetching-failed/restart`,
        {},
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data.data;
    } catch (error) {
      console.error(
        '❌ POST /fe-management-system/token-states/fetching-failed/restart 에러:',
        error,
      );
      throw error;
    }
  },

  /**
   * 토큰 컨텐츠를 업데이트하고 FETCHING_DATA 상태로 전환합니다.
   * @param tokenAddress 토큰 주소
   * @returns 업데이트된 Token State
   */
  async updateTokenContents(tokenAddress: string): Promise<TokenState> {
    try {
      const response: AxiosResponse<any> = await apiClient.patch(
        `/fe-management-system/token-states/${tokenAddress}/contents`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error(
        `❌ PATCH /fe-management-system/token-states/${tokenAddress}/contents 에러:`,
        error
      );
      throw error;
    }
  },

  /**
   * 토큰의 트윗 통계 정보를 가져옵니다.
   * @param tokenAddress 토큰 주소
   * @param beforeHours 조회할 시간 범위 (기본값 2시간)
   * @returns 트윗 통계 정보
   */
  async getTweetStats(tokenAddress: string, beforeHours: number = 2): Promise<TweetStatsResponse> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/fe-management-system/token-states/${tokenAddress}/tweet-stats`,
        {
          params: { beforeHours },
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      console.error(
        `❌ GET /fe-management-system/token-states/${tokenAddress}/tweet-stats 에러:`,
        error
      );
      throw error;
    }
  },

  /**
   * 토큰의 트윗 통계 타임라인을 조회합니다.
   * @param tokenAddress 토큰 주소
   * @returns 트윗 통계 타임라인
   */
  async getTweetStatsTimeline(tokenAddress: string): Promise<TweetStatsTimelineResponse> {
    try {
      const response: AxiosResponse<any> = await apiClient.get(
        `/fe-management-system/token-states/${tokenAddress}/tweet-stats-timeline`,
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data.data;
    } catch (error) {
      console.error(
        `❌ GET /fe-management-system/token-states/${tokenAddress}/tweet-stats-timeline 에러:`,
        error,
      );
      throw error;
    }
  },

  /**
   * 토큰의 트윗 통계 추적 설정을 업데이트합니다.
   * @param tokenAddress 토큰 주소
   * @param request tweetIds + syncIntervalMinutes
   * @returns 업데이트 결과
   */
  async updateTweetStatsConfig(
    tokenAddress: string,
    request: UpdateTweetStatsConfigRequest,
  ): Promise<UpdateTweetStatsConfigResponse> {
    try {
      const response: AxiosResponse<any> = await apiClient.put(
        `/fe-management-system/token-states/${tokenAddress}/tweet-stats-config`,
        request,
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data.data ?? response.data;
    } catch (error) {
      console.error(
        `❌ PUT /fe-management-system/token-states/${tokenAddress}/tweet-stats-config 에러:`,
        error,
      );
      throw error;
    }
  },

  // ─── Keyword Management APIs ───────────────────────────────────────

  /**
   * 키워드 자동완성 (Typeahead)
   * @param prefix 검색할 키워드 prefix (예: "elo")
   * @param limit 반환 개수 (default: 10, max: 50)
   * @returns 매칭되는 키워드 목록과 count
   */
  async keywordAutocomplete(
    prefix: string,
    limit?: number,
  ): Promise<KeywordAutocompleteResponse> {
    try {
      const params: Record<string, any> = { prefix };
      if (limit !== undefined) {
        params.limit = limit;
      }

      const response: AxiosResponse<KeywordAutocompleteResponse> = await apiClient.get(
        '/fe-management-system/token-states/keywords/autocomplete',
        {
          params,
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states/keywords/autocomplete 에러:', error);
      throw error;
    }
  },

  /**
   * 전체 키워드 목록 조회 (count 내림차순 정렬)
   * @param limit 반환 개수 (default: 50, max: 100)
   * @returns 전체 키워드 목록과 total
   */
  async getAllKeywords(limit?: number): Promise<GetAllKeywordsResponse> {
    try {
      const params: Record<string, any> = {};
      if (limit !== undefined) {
        params.limit = limit;
      }

      const response: AxiosResponse<GetAllKeywordsResponse> = await apiClient.get(
        '/fe-management-system/token-states/keywords',
        {
          params,
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states/keywords 에러:', error);
      throw error;
    }
  },

  /**
   * 키워드 기반 토큰 검색
   * @param keywords 검색할 키워드들 (콤마 구분 문자열, 예: "elon,mars")
   * @param mode AND / OR (default: AND)
   * @param limit 반환 개수 (default: 20, max: 100)
   * @returns 매칭된 토큰 목록과 total
   */
  async searchTokensByKeywords(
    keywords: string,
    mode?: 'AND' | 'OR',
    limit?: number,
  ): Promise<SearchTokensByKeywordsResponse> {
    try {
      const params: Record<string, any> = { keywords };
      if (mode !== undefined) {
        params.mode = mode;
      }
      if (limit !== undefined) {
        params.limit = limit;
      }

      const response: AxiosResponse<SearchTokensByKeywordsResponse> = await apiClient.get(
        '/fe-management-system/token-states/keywords/search',
        {
          params,
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('❌ GET /fe-management-system/token-states/keywords/search 에러:', error);
      throw error;
    }
  },

  /**
   * 토큰에 키워드 설정 (덮어쓰기)
   * @param tokenAddress 토큰 주소
   * @param keywords 설정할 키워드 배열 (최대 10개, 각 최대 30자)
   * @returns 업데이트 결과 (추가/제거된 키워드 포함)
   */
  async updateTokenKeywords(
    tokenAddress: string,
    keywords: string[],
  ): Promise<UpdateTokenKeywordsResponse> {
    try {
      const response: AxiosResponse<UpdateTokenKeywordsResponse> = await apiClient.put(
        `/fe-management-system/token-states/${tokenAddress}/keywords`,
        { keywords },
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        `❌ PUT /fe-management-system/token-states/${tokenAddress}/keywords 에러:`,
        error,
      );
      throw error;
    }
  },
};
